import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { authUtil } from 'src/utils';

/**
 * Data layer for the audiobook beta-teaser (`/api/v2/books/:bookId/audiobook/*`).
 *
 * These endpoints are deliberately NOT behind the `audiobook` feature flag — the
 * teaser is shown to authors who don't have the feature yet. They ARE author-only
 * (strict auth). Nothing here charges money: the modal captures paid-INTENT only.
 * See docs/features/audiobooks/beta-teaser.md.
 */

export type AudiobookBetaStatus =
  | 'viewed'
  | 'pending'
  | 'accepted'
  | 'declined'
  | null;

export type AudiobookBetaQuote = {
  bookId: string;
  title: string;
  /** A/B arm (deterministic per user) — snapshotted on any request. */
  variant: string;
  wordCount: number;
  /** Whole-book estimated production cost. Never charged. */
  credits: number;
  usd: number;
  alreadyRequested: boolean;
  status: AudiobookBetaStatus;
};

const apiBase = () =>
  `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}`;

const betaFetch = async <T>(
  bookId: string,
  path: string,
  init: RequestInit = {}
): Promise<T> => {
  const response = await fetch(
    `${apiBase()}/books/${bookId}/audiobook${path}`,
    {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        'br-token': await authUtil.getFreshIdToken(),
        ...(init.headers || {}),
      },
    }
  );
  if (!response.ok) {
    const body = await response.json().catch(() => ({} as any));
    // The API sends human text as `msg` and the machine code as `error`.
    throw new Error(
      body.msg || body.error || response.statusText || 'Request failed'
    );
  }
  if (response.status === 204) return undefined as unknown as T;
  return response.json();
};

export const audiobookBetaKeys = {
  quote: (bookId: string) => ['audiobookBeta', 'quote', bookId] as const,
};

/**
 * Fetch the per-book beta price quote. `enabled` lets the caller defer the fetch
 * until the modal actually opens (so it never fires for users routed straight to
 * the real feature).
 */
export const useAudiobookBetaQuote = (
  bookId?: string,
  options?: { enabled?: boolean }
) =>
  useQuery<AudiobookBetaQuote>(
    audiobookBetaKeys.quote(bookId || ''),
    () => betaFetch<AudiobookBetaQuote>(bookId as string, '/beta-quote'),
    {
      enabled: Boolean(bookId) && options?.enabled !== false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60,
      retry: 1,
    }
  );

/** Record an impression (fire once when the modal opens). Best-effort. */
export const useRecordAudiobookBetaView = () =>
  useMutation((bookId: string) =>
    betaFetch<void>(bookId, '/beta-view', { method: 'POST' })
  );

/** Submit the paid-intent request (the "Request beta access" CTA). */
export const useRequestAudiobookBeta = (bookId?: string) => {
  const queryClient = useQueryClient();
  return useMutation(
    (vars: { message?: string }) =>
      betaFetch<AudiobookBetaQuote>(bookId as string, '/beta-request', {
        method: 'POST',
        body: JSON.stringify({ message: vars.message || '' }),
      }),
    {
      onSuccess: () => {
        if (bookId) {
          queryClient.invalidateQueries(audiobookBetaKeys.quote(bookId));
        }
      },
    }
  );
};
